import React, { useState } from 'react';
import { useDecisionContext } from '../context/DecisionContext';
import { Decision, DecisionTree } from '../models/decisionTree';

// 获取概率颜色，复用于节点样式
const getProbabilityColor = (probability: number): string => {
  if (probability >= 80) return '#22c55e'; // green-500
  if (probability >= 60) return '#4ade80'; // green-400
  if (probability >= 50) return '#facc15'; // yellow-400
  if (probability >= 30) return '#fb923c'; // orange-400
  return '#ef4444'; // red-500
};

// 获取渐变颜色
const getGradientColors = (probability: number): {light: string, dark: string} => {
  if (probability >= 80) return {light: '#86efac', dark: '#16a34a'}; // green
  if (probability >= 60) return {light: '#a7f3d0', dark: '#10b981'}; // emerald
  if (probability >= 50) return {light: '#fef08a', dark: '#eab308'}; // yellow
  if (probability >= 30) return {light: '#fed7aa', dark: '#f97316'}; // orange
  return {light: '#fecaca', dark: '#dc2626'}; // red
};

// 节点列表视图组件
const NodeView: React.FC<{
  tree: DecisionTree;
}> = ({ tree }) => {
  return (
    <div className="space-y-4">
      {tree.decisions.map((decision) => (
        <div key={decision.id} className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-start">
            <div
              className="flex-shrink-0 w-14 h-9 rounded-md flex items-center justify-center text-white font-bold mr-3"
              style={{ backgroundColor: getProbabilityColor(decision.probability) }}
            >
              {decision.probability}%
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-800">{decision.title}</h3>
              <p className="text-gray-600 mt-2 text-sm">{decision.description}</p>
              
              {decision.children.length > 0 && (
                <div className="mt-3 pl-4 border-l-2 border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">子决策:</p>
                  {decision.children.map(child => (
                    <div key={child.id} className="mb-3">
                      <div className="flex items-center">
                        <div
                          className="w-10 h-7 rounded flex items-center justify-center text-white text-xs font-medium mr-2"
                          style={{ backgroundColor: getProbabilityColor(child.probability) }}
                        >
                          {child.probability}%
                        </div>
                        <span className="font-medium text-sm">{child.title}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
  
// 主组件
const DecisionFlowView: React.FC<{ readOnlyTree?: DecisionTree }> = ({ readOnlyTree }) => {
  const { decisionTree } = useDecisionContext();
  
  // 确定使用的决策树
  const displayTree = readOnlyTree || decisionTree;
  
  // 处理空状态
  if (!displayTree || !displayTree.decisions || displayTree.decisions.length === 0) {
    return (
      <div className="w-full max-w-6xl mt-4">
        <div className="bg-white p-8 rounded-lg shadow-sm border text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">暂无决策数据</h3>
          <p className="mt-2 text-gray-500">请先生成决策树，或者选择一个已保存的决策记录</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-full max-w-6xl mt-4">
      <div className="bg-white p-6 rounded-xl shadow-md border border-indigo-50">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-800 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
            </svg>
            决策列表
          </h3>
        </div>
        <NodeView tree={displayTree} />
      </div>
    </div>
  );
};

export default DecisionFlowView; 